/**
 * Resolve a step's body layout — the ordered list of primitives that compose it — from DB config.
 *
 * Layout is authored as a `step_layout` content block (one per step), whose payload names the ordered
 * primitives the body renders:
 *
 *   category: 'step_layout'
 *   key:      '<step kind or step key>'
 *   payload:  { stepKind?: string, primitives: Array<{ key: string; config?: object }> }
 *
 * The body is exactly those primitives, in order. A step with no seeded `step_layout` block resolves to
 * an empty layout and renders nothing — the database is the sole source of truth for what a step body
 * is (there is no bundled whole-body fallback).
 */
import { resolveContentBlocks } from '../../api/campaignData';
import type { StepDef } from '../../config/steps';

/** One primitive reference in a step's body, in render order. */
export interface LayoutEntry {
  /** The registered primitive key (see `primitives/registry`). */
  primitive: string;
  /** Author-supplied config passed to the primitive. */
  config?: Record<string, unknown>;
}

/** The `step_layout` block that targets `step`, matched by payload `stepKind` first, then by `key`. */
function layoutBlockFor(step: StepDef): { primitives?: unknown } | null {
  const blocks = resolveContentBlocks('step_layout');
  const byStepKind = blocks.find((b) => String(b.payload?.stepKind ?? '') === step.kind);
  const byKey =
    byStepKind ?? blocks.find((b) => b.key === step.kind || b.key === step.slug);
  return (byKey?.payload as { primitives?: unknown }) ?? null;
}

/**
 * The ordered primitives composing `step`'s body, from the seeded `step_layout` block. Empty when the
 * step has no authored layout.
 */
export function resolveStepLayout(step: StepDef): LayoutEntry[] {
  const payload = layoutBlockFor(step);
  const raw = Array.isArray(payload?.primitives) ? (payload!.primitives as unknown[]) : [];
  const entries: LayoutEntry[] = [];
  for (const item of raw) {
    if (typeof item === 'string') {
      entries.push({ primitive: item });
      continue;
    }
    if (item && typeof item === 'object') {
      const rec = item as Record<string, unknown>;
      const key = typeof rec.key === 'string' ? rec.key : typeof rec.primitive === 'string' ? rec.primitive : '';
      if (key) {
        entries.push({
          primitive: key,
          config: (rec.config as Record<string, unknown>) ?? undefined,
        });
      }
    }
  }
  return entries;
}
